import { FC, useState } from 'react';
import {
	Table,
	Form,
	Row,
	Col,
	Select,
	Input,
	Button,
	Divider,
	Space,
} from 'antd';
import { ArrowRightOutlined, FileSearchOutlined } from '@ant-design/icons';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import { useApp } from '@/App';
import {
	SearchAgreementPrintingParam,
	getAgreementDocumentDetail,
	useGetAgreementPrintingSearchOption,
	usePreviewMultpleAgreementPrintingMutation,
	useSearchAgreementPrinting,
	useUploadAgreementDocumentMutation,
} from '@/libs/fleet-api/services/print';
import { formatCurrency } from '@/libs/antd/formater';
import { useUserData } from '@/libs/fleet-api/hooks';
import UploadAgreementModal, {
	useUploadAgreementModal,
} from './UploadAgreementModal';
import { useModals } from '@/components/modals/ModalProvider';
import { DocumentItem } from '@/components/modals/DocumentModal';
// import { getDocumentType } from '@/components/modals/PreviewModal';
import PreviewModal, {
	PreviewModalState,
	getDocumentType,
} from '@/components/modals/PreviewModal';
import FleetTitle from '@/libs/antd/components/Title';

const PrintListPage: FC = () => {
	const { navigate } = useApp();
	const {
		modal,
		openDocumentModal,
		openCustomerDetail,
		openPreviewDocumentModal,
	} = useModals();
	const userData = useUserData();
	const { groupName } = userData ?? {};

	const [queryParams, setQueryParams] = useState<SearchAgreementPrintingParam>({
		pagination: {
			pageNumber: 1,
			pageSize: 10,
		},
	});

	const { data, isLoading, isFetching, refetch } =
		useSearchAgreementPrinting(queryParams);

	const agreementPrintingSearchOptionQuery =
		useGetAgreementPrintingSearchOption();

	const previewMultpleAgreementPrintingMutation =
		usePreviewMultpleAgreementPrintingMutation();

	const [form] = Form.useForm();

	const dataSource = data?.data.items ?? [];

	const uploadAgreementModal = useUploadAgreementModal();
	const uploadAgreementDocumentMutation = useUploadAgreementDocumentMutation();

	const [previewFile, setPreviewFile] = useState<PreviewModalState>(null);

	return (
		<Space
			direction="vertical"
			style={{ display: 'flex' }}
			size={'large'}
		>
			<UploadAgreementModal {...uploadAgreementModal} />
			<FleetTitle>Pencetakan Perjanjian Pembiayaan</FleetTitle>
			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={form}
				layout="horizontal"
				colon={false}
				labelAlign="left"
				onFinish={(values) => {
					setQueryParams((params) => ({
						...params,
						frmId: values.frmId,
						fbdhId: values.fbdhId,
						plafonTypeId: values.plafonTypeId,
						activityStatusId: values.activityStatusId,
						divisionId: values.divisionId,
						financingLimitTypeId: values.financingLimitTypeId,
						search: values.searchValue
							? {
								by: values.searchBy,
								value: values.searchValue,
							}
							: undefined,
						pagination: {
							pageNumber: 1,
							pageSize: params.pagination.pageSize,
						},
					}));
				}}
			>
				<Form.Item
					noStyle
					shouldUpdate
				>
					{({ getFieldValue }) => {
						const searchBy = getFieldValue('searchBy') as string;

						const renderSearch = () => {
							if (searchBy === 'frmId') {
								return (
									<Form.Item name={'frmId'}>
										<Select
											options={
												agreementPrintingSearchOptionQuery.data?.frmOptions?.map(
													(option) => {
														return {
															value: option.id,
															label: option.value,
														};
													},
												) ?? []
											}
											placeholder="Select FRM"
											style={{ width: '100%' }}
											allowClear
										/>
									</Form.Item>
								);
							}

							if (searchBy === 'fbdhId') {
								return (
									<Form.Item name={'fbdhId'}>
										<Select
											options={
												agreementPrintingSearchOptionQuery.data?.fbdhOptions?.map(
													(option) => {
														return {
															value: option.id,
															label: option.value,
														};
													},
												) ?? []
											}
											placeholder="Select FBDH"
											style={{ width: '100%' }}
											allowClear
										/>
									</Form.Item>
								);
							}

							if (searchBy === 'plafonTypeId') {
								return (
									<Form.Item name={'plafonTypeId'}>
										<Select
											options={
												agreementPrintingSearchOptionQuery.data?.plafonTypeOptions?.map(
													(option) => {
														return {
															value: option.id,
															label: option.value,
														};
													},
												) ?? []
											}
											placeholder="Select Jenis Plafond"
											style={{ width: '100%' }}
											allowClear
										/>
									</Form.Item>
								);
							}

							if (searchBy === 'divisionId') {
								return (
									<Form.Item name={'divisionId'}>
										<Select
											options={
												agreementPrintingSearchOptionQuery.data?.divisionOptions?.map(
													(option) => {
														return {
															value: option.id,
															label: option.value,
														};
													},
												) ?? []
											}
											placeholder="Select Divisi"
											style={{ width: '100%' }}
											allowClear
										/>
									</Form.Item>
								);
							}

							if (searchBy === 'financingLimitTypeId') {
								return (
									<Form.Item name={'financingLimitTypeId'}>
										<Select
											options={
												agreementPrintingSearchOptionQuery.data?.financingLimitTypeOptions?.map(
													(option) => {
														return {
															value: option.id,
															label: option.value,
														};
													},
												) ?? []
											}
											placeholder="Select Sifat Limit"
											style={{ width: '100%' }}
											allowClear
										/>
									</Form.Item>
								);
							}

							if (searchBy === 'activityStatusId') {
								return (
									<Form.Item name={'activityStatusId'}>
										<Select
											options={
												agreementPrintingSearchOptionQuery.data?.activityStatusOptions?.map(
													(option) => {
														return {
															value: option.id,
															label: option.value,
														};
													},
												) ?? []
											}
											placeholder="Select Status SPF"
											style={{ width: '100%' }}
											allowClear
										/>
									</Form.Item>
								);
							}

							return (
								<Form.Item name="searchValue">
									<Input
										placeholder="Search..."
										style={{
											display: 'block',
										}}
									/>
								</Form.Item>
							);
						};
						return (
							<Row gutter={[20, 20]}>
								<Col flex="none">
									<Form.Item
										name="searchBy"
										label={<span>Search By</span>}
										labelCol={{ flex: 'auto', style: { width: 150 } }}
										wrapperCol={{ flex: 'none', style: { width: 250 } }}
										initialValue="customer-name"
									>
										<Select
											placeholder="Search By"
											options={[
												{
													label: 'Nama Customer',
													value: 'customer-name',
												},
												{
													label: 'Group BMPP',
													value: 'group-bmpp',
												},
												{
													label: 'FRM',
													value: 'frmId',
												},
												{
													label: 'FBDH',
													value: 'fbdhId',
												},
												{
													label: 'Jenis Plafond',
													value: 'plafonTypeId',
												},
												{
													label: 'Divisi',
													value: 'divisionId',
												},
												{
													label: 'Sifat Limit',
													value: 'financingLimitTypeId',
												},

												{
													label: 'Status SPF',
													value: 'activityStatusId',
												},
											]}
											allowClear
											style={{
												display: 'block',
												width: '100%',
											}}
										/>
									</Form.Item>
								</Col>
								<Col flex="auto">{renderSearch()}</Col>
								<Col>
									<Form.Item>
										<Button
											block
											type="primary"
											htmlType="submit"
											loading={isLoading || isFetching}
										>
											SEARCH
										</Button>
									</Form.Item>
								</Col>
							</Row>
						);
					}}
				</Form.Item>
			</Form>

			<Divider style={{ margin: 0 }} />

			<Table
				bordered
				loading={isLoading || isFetching}
				scroll={{ x: 'max-content' }}
				dataSource={dataSource.map((data, key) => {
					return { ...data, key: data.napId ?? key };
				})}
				columns={[
					{
						title: 'No',
						align: 'center',
						width: 1,
						render: (_, __, index) =>
							(queryParams.pagination.pageNumber - 1) *
							queryParams.pagination.pageSize +
							index +
							1,
					},
					{
						title: 'No NAP',
						render: (_, record) => (
							<a
								href="#"
								onClick={(e) => {
									e.preventDefault();
									if (!record.prospectId) return;

									navigate('NAPPage', {
										prospectId: record.prospectId,
										onBack: () => {
											navigate('PrintListPage');
										},
									});
								}}
							>
								{record.napNo ?? '-'}
							</a>
						),
					},
					{
						title: 'No SPF',
						render: (_, record) => (
							<a
								href="#"
								onClick={(e) => {
									e.preventDefault();
									if (!record.spfId || !record.napId) {
										modal.confirm({
											title: 'Error',
											subTitle:
												'Data SPF tidak lengkap. Silakan refresh halaman dan coba lagi.',
											okText: 'OK',
										});
										return;
									}

									navigate('SPFPage', {
										prospectId: record.prospectId!,
										spfId: record.spfId,
										napResolutionId: record.napId,
										onBack: () => navigate('PrintListPage'),
										viewOnly: true,
									});
								}}
							>
								{record.spfNo ?? '-'}
							</a>
						),
					},
					{
						title: 'Nama Customer',
						render: (_, record) => (
							<a
								onClick={(e) => {
									e.preventDefault();
									openCustomerDetail(record.customerId);
								}}
							>
								{record.customerName}
							</a>
						),
					},
					{
						title: 'Group BMPP',
						render: (_, record) => record.bmppGroupName,
					},
					{
						title: 'FRM',
						render: (_, record) => record.frmName,
					},
					{
						title: 'FBDH',
						render: (_, record) => record.fbdhName,
					},
					{
						title: 'Divisi',
						render: (_, record) => record.divisionName,
					},
					{
						title: 'Jenis Plafond',
						render: (_, record) =>
							record.plafonTypes.map(({ value }) => value).join(', '),
					},
					{
						title: 'Sifat Limit',
						render: (_, record) =>
							record.financingLimitTypes.map(({ value }) => value).join(', '),
					},
					{
						title: 'Jumlah Plafond',
						render: (_, record) =>
							record.plafonAmount ? formatCurrency(record.plafonAmount) : '-',
					},
					{
						title: 'Nomor Perjanjian ',
						hidden: groupName !== 'FCDVS',
						render: (_, record) => record.agreementNo ?? '-',
					},
					{
						title: 'Upload Perjanjian',
						hidden: groupName !== 'FCDVS',
						align: 'center',
						render: (_, record) => {
							// After agreement is printed (has agreementNo), upload should be active
							// After agreement is uploaded (has agreementDocumentId), upload should be inactive
							const disabled = !record.allowUploadAgreement;

							return (
								<a
									href="#"
									onClick={async (e) => {
										e.preventDefault();
										if (disabled) return;

										const payload = await uploadAgreementModal.open({
											id: record.id,
											customerId: record.customerId,
										});

										if (!payload) return;
										await uploadAgreementDocumentMutation.mutateAsync(payload);
										await modal.success({
											title: 'Sukses',
											subTitle: 'Dokumen Perjanjian berhasil di upload',
											okText: 'Kembali',
										});
										refetch();
									}}
								>
									<ArrowRightOutlined
										style={disabled ? { color: '#bababa' } : {}}
									/>
								</a>
							);
						},
					},
					{
						title: 'View Perjanjian',
						hidden: groupName !== 'FCDVS',
						align: 'center',
						render: (_, record) => {
							// View should be active only after agreement document is uploaded (has agreementDocumentId)
							const disabled = !record.allowViewAgreement;

							return (
								<a
									href="#"
									onClick={async (e) => {
										e.preventDefault();
										if (disabled) return;

										const res = await getAgreementDocumentDetail(
											record.id!.toString(),
										);

										const {
											agreementDocument,
											commissionerStatementDocument,
											directorStatementDocument,
											otherDocument,
										} = res.data ?? {};
										const documentItems: DocumentItem[] = [];

										if (agreementDocument) {
											documentItems.push({
												name: 'Dokumen Perjanjian',
												type: getDocumentType(agreementDocument.name)!,
												url: agreementDocument.url,
											});
										}

										if (commissionerStatementDocument) {
											documentItems.push({
												name: 'Dokumen Surat Persetujuan Komisaris/RUPS',
												type: getDocumentType(
													commissionerStatementDocument.name,
												)!,
												url: commissionerStatementDocument.url,
											});
										}

										if (directorStatementDocument) {
											documentItems.push({
												name: 'Dokumen Surat Pernyataan Data Korporasi',
												type: getDocumentType(directorStatementDocument.name)!,
												url: directorStatementDocument.url,
											});
										}

										if (otherDocument) {
											documentItems.push({
												name: 'Dokumen Lainnya',
												type: getDocumentType(otherDocument.name)!,
												url: otherDocument.url,
											});
										}

										openDocumentModal(documentItems);
									}}
								>
									<FileSearchOutlined
										style={disabled ? { color: '#bababa' } : {}}
									/>
								</a>
							);
						},
					},
					{
						title: 'Cetak  Perjanjian',
						hidden: groupName !== 'FCDVS',
						align: 'center',
						render: (_, record) => {
							// Print should be active initially and after printing (has agreementNo)
							// Print should be inactive only after agreement document is uploaded (has agreementDocumentId)
							const disabled = !record.allowPrintAgreement;
							return (
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										if (disabled) return;

										navigate('PrintPage', {
											id: record.spfId,
										});
									}}
								>
									<ArrowRightOutlined
										style={disabled ? { color: '#bababa' } : {}}
									/>
								</a>
							);
						},
					},
					{
						title: 'Download Perjanjian',
						hidden: groupName !== 'FCDVS',
						align: 'center',
						render: (_, record) => {
							// Download should be active after agreement has been printed (has agreementNo)
							// Download should be inactive if no agreement has been printed yet
							const disabled = !record.allowUploadAgreement;

							return (
								<a
									href="#"
									onClick={async (e) => {
										e.preventDefault();
										if (disabled) return;

										try {
											// Use preview mutation to get signed URL for download
											const results =
												await previewMultpleAgreementPrintingMutation.mutateAsync(
													{
														id: record.id,
													},
												);

											if (results?.data?.length) {
												// Open preview modal with download action
												openPreviewDocumentModal({
													url: results.data.map((d) => d.signedUrl),
													allowDownload: false,
													downloadAllDocuments: true,
													zipName: `Dokumen Perjanjian Pembiayaan - ${record.spfNo}`,
													buttonPosition: 'top',
												});
											}
										} catch (error) {
											console.error('Error getting download URL:', error);
											await modal.confirm({
												title: 'Error',
												subTitle:
													'Gagal mengakses dokumen download. Silakan coba lagi.',
												okText: 'OK',
											});
										}
									}}
								>
									<FileSearchOutlined
										style={disabled ? { color: '#bababa' } : {}}
									/>
								</a>
							);
						},
					},
					{
						fixed: 'right',
						title: 'Assign FCDVS',
						hidden: groupName !== 'FCOH',
						align: 'center',
						width: 140,
						render: (_, record) =>
							record.allowAssignFcdvs ? (
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										navigate('PrintPage', {
											id: record.spfId,
										});
									}}
								>
									<ArrowRightOutlined />
								</a>
							) : (
								<ArrowRightOutlined style={{ color: '#bababa' }} />
							),
					},
				]}
				pagination={{
					total: data?.data.totalCount,
					current: queryParams.pagination.pageNumber,
					pageSize: queryParams.pagination.pageSize,
					onChange: (pageNumber, pageSize) => {
						setQueryParams((current) => {
							return {
								...current,
								pagination: {
									pageSize,
									pageNumber:
										current.pagination.pageSize !== pageSize ? 1 : pageNumber,
								},
							};
						});
					},
				}}
			/>
			<PreviewModal
				file={previewFile}
				onClose={() => setPreviewFile(null)}
				action={
					<div
						style={{
							position: 'absolute',
							inset: 'auto 0 40px 0',
							display: 'flex',
							justifyContent: 'center',
							gap: 20,
						}}
					>
						<Button
							type="primary"
							download
							href={previewFile?.url}
							target="_blank"
							style={{
								width: 200,
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
							}}
						>
							DOWNLOAD
						</Button>
					</div>
				}
			/>
		</Space>
	);
};

export default PrintListPage;
